import { useEffect, useState, useRef } from "react";

// Redux
import { useDispatch, useSelector } from 'react-redux';
import {
    showWaste,
    removeWaste,
    generateIncome
} from '../../../store/actions/waste';

import { setYear, setQuarter } from '../../../store/reducers/uiSlice';
import { IoChevronDownOutline } from 'react-icons/io5';

// Components
import {
    Table,
    Row,
    Col,
    Pagination,
    Button,
    Modal,
    OverlayTrigger,
    Tooltip
} from 'react-bootstrap';

import { formatDate, currentYear, formatNumber, truncateText, isSubscriptionActive, shouldShowPaymentAlert } from "../../../utils/tools";

import EditWaste from "./edit";
import AddIncomeWaste from './addIncome.js';
import AddDisposalWaste from './addDisposal.js';
import MonthlyIncomeGenerator from "./monthlyIncomeGenerator.js";

// Modals
import CustomModalInline from "../../../hoc/customModalInline";

// Assets
import { ReactComponent as EditIcon } from '../../../assets/edit.svg';
import { ReactComponent as DuplicateIcon } from '../../../assets/duplicate.svg';
import { ReactComponent as RemoveIcon } from '../../../assets/remove.svg';

const Detail = ({evidenceListId, branchId}) => {
    const dispatch = useDispatch();

    const tenant = useSelector(state => state.tenant.current);
    const wastes = useSelector(state => state.waste.wastes);
    const meta = useSelector(state => state.waste.meta);
    const currentWaste = useSelector(state => state.waste.currentWaste);

    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isAddIncomeModalVisible, setIsAddIncomeModalVisible] = useState(false);
    const [isAddDisposalModalVisible, setIsAddDisposalModalVisible] = useState(false);
    const [isMonthlyIncomeGenModalVisible, setIsMonthlyIncomeGenVisible] = useState(false);
    const [wasteToEdit, setWasteToEdit] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showConfirmDropdownModal, setShowConfirmDropdownModal] = useState(false);
    const [wasteToDelete, setWasteToDelete] = useState(null);
    const [copiedData, setCopiedData] = useState(null);

    // Default init values
    const [page, setPage] = useState(1);
    const limit = 25;

    const year = useSelector((state) => state.uiSlice.selectedYear);
    const quarter = useSelector((state) => state.uiSlice.selectedQuarter);

    const isActive = isSubscriptionActive(tenant?.subscriptionStatus, tenant?.plan);

    useEffect(() => {
        if (evidenceListId && branchId) {
            dispatch(showWaste({ evidenceListId, branchId, page, limit, year, quarter }))      
        } 
    }, [evidenceListId, branchId, page, limit, year, quarter, currentWaste]);

    // Handlers
    const handleQuarterChange = (eventKey) => {
        const selectedQuarter = parseInt(eventKey);
        dispatch(setQuarter(selectedQuarter));
        setPage(1); // Reset na prvú stránku pri zmene filtra
    };

    const handleYearChange = (eventKey) => {
        const selectedYear = parseInt(eventKey);
        dispatch(setYear(selectedYear));
        setPage(1); // Reset na prvú stránku pri zmene filtra
    };

    const handlePageChange = (pageNumber) => {
        setPage(pageNumber);
    };

    const handleAddIncomeButttonClick = (copiedWasteData) => {
        setIsAddIncomeModalVisible(true);
        setIsAddDisposalModalVisible(false);
        if(copiedWasteData)
            setCopiedData(copiedWasteData);
    }

    // Po úspešnom pridaní/duplikovaní odpadu zobraz "všetky kvartály" na 1. strane,
    // aby bol novopridaný záznam vždy viditeľný. Samotný refetch tabuľky zabezpečí
    // efekt vyššie (currentWaste dependency) - nedispatchuj showWaste aj tu, aby
    // nevznikla súbežná (race) požiadavka s odlišnými filtrami, ktorá vie prepísať
    // výsledok tou nesprávnou (a v tabuľke sa krátko zobrazia prázdne hodnoty).
    const handleWasteAdded = () => {
        setPage(1);
        dispatch(setQuarter(0));
    }

    const handleGenerateIncomeButttonClick = () => {
        dispatch(generateIncome({branchId, evidenceListId, year}));
        setShowConfirmDropdownModal(false);
    }

    const handleAddDisposalButttonClick = (copiedWasteData) => {
        setIsAddDisposalModalVisible(true);
        setIsAddIncomeModalVisible(false);
        if(copiedWasteData)
            setCopiedData(copiedWasteData);
    }

    const handleEditButtonClick = (wasteId) => {
        setWasteToEdit(wasteId);
        setIsEditModalVisible(true);
    }

    const handleDeleteButtonClick = (waste) => {

        setWasteToDelete(waste);
        setShowConfirmModal(true);

    };

    const handleGenerateIncomeLikeOutcomeButtonClick = () => {
        setShowConfirmDropdownModal(true);

    };

    const handleMonthlyIncomeGenButtonClick = () => {
        setIsMonthlyIncomeGenVisible(true);
    };

    const handleCopyWasteData = (copiedWasteData) => {
        if(copiedWasteData.disposal !== 0 && copiedWasteData.income === 0)
            handleAddDisposalButttonClick(copiedWasteData);
        else
            handleAddIncomeButttonClick(copiedWasteData);
    }

    const handleConfirmDelete = () => {
        const wasteId = wasteToDelete._id;
        dispatch(removeWaste({ wasteId, evidenceListId, branchId }))
            .then(() => {
                if (evidenceListId && branchId) {
                    dispatch(showWaste({ evidenceListId, branchId, page, limit, year, quarter }));
                    handleCloseConfirmModal(); 
                }
            })
            .catch(error => {
                console.error("Error removing waste:", error);
                handleCloseConfirmModal(); 
            });

    };

    const handleCloseEditModal = () => {
        setIsEditModalVisible(false);
        setWasteToEdit(null);
    };

    const handleCloseAddIncomeModal = () => {
        setIsAddIncomeModalVisible(false);
        setCopiedData(null);
    };

    const handleCloseAddDisposalModal = () => {
        setIsAddDisposalModalVisible(false);
        setCopiedData(null);
    };

    const handleCloseConfirmModal = () => {
        setShowConfirmModal(false);
        setWasteToDelete(null);
    };

    const handleCloseConfirmDropdownModal = () => {
        setShowConfirmDropdownModal(false);
    }

    const handleCloseMonthlyIncomeGenModal = () => {
       setIsMonthlyIncomeGenVisible(false);
    };

    
    const thisYear = currentYear();
    const years = [thisYear, thisYear - 1, thisYear - 2, thisYear - 3, thisYear - 4];

    const [quarterOpen, setQuarterOpen] = useState(false);
    const [yearOpen, setYearOpen] = useState(false);
    const [generateOpen, setGenerateOpen] = useState(false);
    const quarterRef = useRef(null);
    const yearRef = useRef(null);
    const generateRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (quarterRef.current && !quarterRef.current.contains(e.target)) setQuarterOpen(false);
            if (yearRef.current && !yearRef.current.contains(e.target)) setYearOpen(false);
            if (generateRef.current && !generateRef.current.contains(e.target)) setGenerateOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const quarters = [
        { key: 0, label: 'Všetky kvartály' },
        { key: 1, label: '1. kvartál' },
        { key: 2, label: '2. kvartál' },
        { key: 3, label: '3. kvartál' },
        { key: 4, label: '4. kvartál' },
    ];

    // Render Tooltips
    const renderTooltipEdit = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Upraviť odpad
        </Tooltip>
    );
    
    const renderTooltipCopy = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Duplikovať odpad
        </Tooltip>
    );

    const renderTooltipRemove = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Vymazať odpad
        </Tooltip>
    );

    const renderTooltipWarehouse = (props, warehouse) => (
        <Tooltip id="button-tooltip" {...props}>
            {warehouse}
        </Tooltip>
    );

    const renderTooltipGenerateIncome = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Vypočítať a pridať vznik odpadu podľa nakladania odpadu. 
        </Tooltip>
    );


    return (
        <>
            <div className="content pb-4">

                <div className="waste-toolbar">
                    <div className="d-flex gap-3">

                        <div className="notification-center" ref={quarterRef}>
                            <button className="select-branch-trigger" onClick={() => setQuarterOpen(o => !o)}>
                                <span>{quarter === 0 ? 'Všetky kvartály' : `${quarter}. kvartál`}</span>
                                <IoChevronDownOutline size={14} className={`select-branch-chevron${quarterOpen ? ' open' : ''}`} />
                            </button>
                            {quarterOpen && (
                                <div className="notification-dropdown select-branch-dropdown">
                                    <div className="notification-header">Kvartál</div>
                                    {quarters.map(q => (
                                        <button
                                            key={q.key}
                                            className={`user-menu-item select-branch-item${quarter === q.key ? ' active' : ''}`}
                                            onClick={() => { handleQuarterChange(String(q.key)); setQuarterOpen(false); }}
                                        >
                                            {q.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="notification-center" ref={yearRef}>
                            <button className="select-branch-trigger" onClick={() => setYearOpen(o => !o)}>
                                <span>{year}</span>
                                <IoChevronDownOutline size={14} className={`select-branch-chevron${yearOpen ? ' open' : ''}`} />
                            </button>
                            {yearOpen && (
                                <div className="notification-dropdown select-branch-dropdown">
                                    <div className="notification-header">Rok</div>
                                    {years.map(y => (
                                        <button
                                            key={y}
                                            className={`user-menu-item select-branch-item${year === y ? ' active' : ''}`}
                                            onClick={() => { handleYearChange(String(y)); setYearOpen(false); }}
                                        >
                                            {y}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>

                    <div className="d-flex gap-3">

                        <div className="notification-center" ref={generateRef}>
                            <button
                                className="action-trigger"
                                onClick={() => setGenerateOpen(o => !o)}
                                disabled={!isActive}
                            >
                                <span>Vygenerovať vznik</span>
                                <IoChevronDownOutline size={14} className={`select-branch-chevron${generateOpen ? ' open' : ''}`} />
                            </button>
                            {generateOpen && (
                                <div className="notification-dropdown select-branch-dropdown">
                                    <div className="notification-header">Vygenerovať vznik</div>
                                    <button
                                        className="user-menu-item"
                                        onClick={() => { handleMonthlyIncomeGenButtonClick(); setGenerateOpen(false); }}
                                    >
                                        Vygenerovať vznik po mesiacoch
                                    </button>
                                    <OverlayTrigger placement="bottom" overlay={renderTooltipGenerateIncome}>
                                        <button
                                            className="user-menu-item"
                                            onClick={() => { handleGenerateIncomeLikeOutcomeButtonClick(); setGenerateOpen(false); }}
                                        >
                                            Vygenerovať vznik podľa nakladania
                                        </button>
                                    </OverlayTrigger>
                                </div>
                            )}
                        </div>

                        <Button
                            className="btn-custom-small"
                            onClick={() => { setCopiedData(null); handleAddIncomeButttonClick(); }}
                            disabled={!isActive}
                        >
                            Pridať vznik/príjem
                        </Button>

                        <Button
                            className="btn-custom-small"
                            onClick={() => { setCopiedData(null); handleAddDisposalButttonClick(); }}
                            disabled={!isActive}
                        >
                            Pridať nakladanie
                        </Button>

                    </div>
                </div>

                { wastes && wastes.length > 0 ? (
                    <Table responsive hover size="" className="table-waste">
                        <thead>
                            <tr>
                                <th className="px-3">Dátum</th>
                                <th>Sklad</th>
                                <th>Nádoba</th>
                                <th>Vznik/príjem</th>
                                <th>Nakladanie</th>
                                <th>Kód</th>
                                <th>Odberateľ/Dodávateľ</th>
                                <th>Skratka</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {wastes.map((waste) => {
                                const rowClass = waste.disposal > 0 ? 'disposal-positive' : 'income-positive';
                                const fmt = (val) => val > 0
                                    ? new Intl.NumberFormat('sk-SK', { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(formatNumber(val))
                                    : '—';
                                return (
                                    <tr key={waste._id} className={`without-top-padding ${rowClass}`}>
                                        <td className="px-3">{formatDate(waste.date)}</td>
                                        {waste.warehouse ? (
                                            <OverlayTrigger placement="top" overlay={(props) => renderTooltipWarehouse(props, waste.warehouse)}>
                                                <td className="waste-warehouse-cell">{waste.warehouse}</td>
                                            </OverlayTrigger>
                                        ) : (
                                            <td></td>
                                        )}
                                        {waste.container ? (
                                            <OverlayTrigger placement="top" overlay={(props) => renderTooltipWarehouse(props, waste.container)}>
                                                <td className="waste-container-cell">{waste.container}</td>
                                            </OverlayTrigger>
                                        ) : (
                                            <td></td>
                                        )}
                                        <td>{fmt(waste.income)}</td>
                                        <td>{fmt(waste.disposal)}</td>
                                        <td>{waste.loadingCode}</td>
                                        {waste.client?.companyName ? (
                                            <OverlayTrigger placement="top" overlay={(props) => renderTooltipWarehouse(props, waste.client.companyName)}>
                                                <td className="waste-client-cell">{waste.client.companyName}</td>
                                            </OverlayTrigger>
                                        ) : (
                                            <td></td>
                                        )}
                                        <td>{waste.acronym}</td>
                                        <td className="waste-actions">
                                            <Button onClick={() => handleEditButtonClick(waste._id)} className="btnIcon">
                                                <OverlayTrigger placement="top" overlay={renderTooltipEdit}>
                                                    <EditIcon alt="Upraviť" className="icon icon-edit" />
                                                </OverlayTrigger>
                                            </Button>
                                            <Button onClick={() => handleCopyWasteData(waste)} className="btnIcon">
                                                <OverlayTrigger placement="top" overlay={renderTooltipCopy}>
                                                    <DuplicateIcon alt="Kopírovať" className="icon icon-copy" />
                                                </OverlayTrigger>
                                            </Button>
                                            <Button onClick={() => handleDeleteButtonClick(waste)} className="btnIcon">
                                                <OverlayTrigger placement="top" overlay={renderTooltipRemove}>
                                                    <RemoveIcon alt="Vymazať" className="icon icon-remove" />
                                                </OverlayTrigger>
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                ) : (
                    <div className="waste-empty-state">
                        <div className="waste-empty-title">Žiadne záznamy</div>
                        <div className="waste-empty-text">Vybrané obdobie neobsahuje žiadne odpady.</div>
                    </div>
                )}

                {/* Pagination for wastes */}
                <div className="pagination-container px-3">
                    <Pagination>
                        {Array.from({ length: meta.totalPages }, (_, i) => (
                            <Pagination.Item
                                key={i + 1}
                                active={i + 1 === page}
                                onClick={() => handlePageChange(i + 1)}
                            >
                                {i + 1}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                </div>

                {/* Panel for editing client */}
                <CustomModalInline isVisible={isEditModalVisible} onClose={handleCloseEditModal}>
                {
                    wasteToEdit && isEditModalVisible ?
                        <EditWaste wasteToEdit={wasteToEdit} onClose={handleCloseEditModal} />
                        : null
                }
                </CustomModalInline>

                {/* Panel for add waste type income */}
                <CustomModalInline isVisible={isAddIncomeModalVisible} onClose={handleCloseAddIncomeModal}>
                {
                    <AddIncomeWaste
                        branchId={branchId}
                        evidenceListId={evidenceListId}
                        onClose={handleCloseAddIncomeModal}
                        onAdded={handleWasteAdded}
                        copiedData={copiedData}
                    />
                }
                </CustomModalInline>

                {/* Panel for add waste type disposal */}
                <CustomModalInline isVisible={isAddDisposalModalVisible} onClose={handleCloseAddDisposalModal}>
                {
                    <AddDisposalWaste
                        branchId={branchId}
                        evidenceListId={evidenceListId}
                        onClose={handleCloseAddDisposalModal}
                        onAdded={handleWasteAdded}
                        copiedData={copiedData}
                    />
                }
                </CustomModalInline>

                {/* Panel for monthly income generator */}
                <CustomModalInline isVisible={isMonthlyIncomeGenModalVisible} onClose={handleCloseMonthlyIncomeGenModal}>
                {
                    <MonthlyIncomeGenerator
                        branchId={branchId}
                        evidenceListId={evidenceListId}
                        onClose={handleCloseMonthlyIncomeGenModal}
                    />
                }
                </CustomModalInline>

                {/* Confirmation modal - REMOVE */}
                <Modal
                    show={showConfirmModal}
                    onHide={handleCloseConfirmModal}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Potvrdenie</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Naozaj chcete vymazať odpad?
                        { wasteToDelete ? 
                             <div style={{ marginTop: '10px' }}>
                                <div style={{ marginBottom: '5px' }}><b>Dátum:</b> {formatDate(wasteToDelete.date)}</div>
                                <div style={{ marginBottom: '5px' }}><b>Sklad:</b> {wasteToDelete.warehouse}</div>
                                <div style={{ marginBottom: '5px' }}><b>Nádoba:</b> {wasteToDelete.container}</div>
                                <div style={{ marginBottom: '5px' }}><b>Vznik/príjem:</b> {wasteToDelete.income}</div>
                                <div style={{ marginBottom: '5px' }}><b>Nakladanie:</b> {wasteToDelete.disposal}</div>
                                <div style={{ marginBottom: '5px' }}><b>Kód nakladania:</b> {wasteToDelete.loadingCode}</div>
                                <div style={{ marginBottom: '5px' }}><b>Odberateľ/Dodávateľ:</b> {wasteToDelete.client?.companyName ?? '—'}</div>
                                <div style={{ marginBottom: '5px' }}><b>Skratka:</b> {wasteToDelete.acronym}</div>
                            </div>
                        : null }    
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseConfirmModal}>
                            Zrušiť
                        </Button>
                        <Button variant="danger" onClick={handleConfirmDelete}>
                            Áno, vymazať
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Confirmation modal Generate income by outcome */}
                <Modal
                    show={showConfirmDropdownModal}
                    onHide={handleCloseConfirmDropdownModal}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Potvrdenie</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Naozaj chcete vygenerovať vznik odpadu podľa nakladania odpadu?

                        Vznik odpadu bude vygenerovaný s rovnakou hmotnosťou a dátumom ako nakladanie s odpadom.
                        Pokiaľ už takýto vznik odpadu existuje, tak nebude vygenerovaný.
                        
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseConfirmDropdownModal}>
                            Zrušiť
                        </Button>
                        <Button variant="danger" onClick={handleGenerateIncomeButttonClick}>
                            Áno, vygenerovať vznik
                        </Button>
                    </Modal.Footer>
                </Modal>

            </div>
            
        </>
    );
};

export default Detail;
